import { Timeline, OverlayType, Projection } from './timeline';
import { TimelineRange, CursorMillis } from './timeline-common';
import { Size } from '../data/common';
import { delegatedObserver, markup2element } from '../dom/dom';
import { Subscription } from 'rxjs';

import { Datum, DataMap, TimelineOverlayDatumReader, TemporalCoordinate } from '../data-source/types';
import { VisualId, createElemSelector } from '../data-source/visualizer';

const overlayDomMap = new Map<string, any>();

export enum TimelineOverlayCategory {
	BOTTOM = "bottom",
	OBSERVATION = "observation",
	OBSERVATION_TRAJECT = "observation_traject",
	MODELLING_PROJECT = "modelling_project",
	SENSOR = "sensor",
	TOP = "top"
}

const timelineOverlayStackOrder: string[] = [
	TimelineOverlayCategory.BOTTOM,
	TimelineOverlayCategory.MODELLING_PROJECT,
	TimelineOverlayCategory.OBSERVATION,
	TimelineOverlayCategory.OBSERVATION_TRAJECT,
	TimelineOverlayCategory.SENSOR,
	TimelineOverlayCategory.TOP
]

const getStackOrder = (category: string | TimelineOverlayCategory): number => {
	let zOrder = timelineOverlayStackOrder.indexOf(category);
	return timelineOverlayStackOrder.length - 1 - (zOrder >= 0 ? zOrder : 0);
}


export const getOverlay = (elem: HTMLElement): any => {
	return overlayDomMap.get(elem.id);
}
export const connectOverlay = (elem: HTMLElement, overlay: any): any => {
	return overlayDomMap.set(elem.id, overlay);
}
export const disconnectOverlay = (elem: HTMLElement): any => {
	return overlayDomMap.delete(elem.id);
}

export interface TimelineRenderer {
	size(): Size,
	draw(domElem: HTMLElement, meta: TimelineItemMetaData, minimumSize: Size, animationScript: CursorMillis[], localProjection: (millis: number) => number): void
}

export interface TimelineOverlayOptions {
	autoLayer?: boolean,// defaults to true
	noTimelineNotifications?: boolean,//defaults to false
	elementData?: { [key: string]: string },
	eventHandlers?: { [key: string]: (evAndTrgt: { event: Event, target: HTMLElement }, data?: any) => void }
}

export interface TimelineOverlayContentProducerOptions {
	layout?: TimelineLayout,
	padding?: number,
	marginGroup?: number,
	marginRow?: number
}

export interface TimelineLayoutResult {
	tlItems: Map<string, TimelineItemMetaData[][]>,
	dataItem2tlItemMap: Map<string, number[]>,
	tlItemList: VisualId[]
}

export interface TimelineOverlayContentProducer {
	draw: (container: HTMLElement, className: string, timelineRange: TimelineRange, timelineProjection: Projection, animationTiming: CursorMillis[]) => VisualId[],
	getHeight: () => number
}
export interface TimelineOverlayDataContentProducer extends TimelineOverlayContentProducer {
	setData: (data: DataMap) => void,
	setAnimationPosition: (animationPosition: CursorMillis) => void,
	getItem: (elemId: string) => { group: string, item: TimelineItemMetaData },
	findItem: (datumId: string) => { group: string, item: TimelineItemMetaData },
	highlight: (container: HTMLElement) => void
}


/**
 * The base timeline overlay
 */

export class OverlayView {
	private __id: string;
	private _timeline: Timeline;
	private _layer: HTMLElement;

	private subscriptions: Subscription[] = [];
	private categoryStackOrder: number;

	/**
	 * @param autoLayer when set to true, a layer will be automatically be added to the timeline 
	 * and onAdd() and onRemove() do most likely not have to be overrriden. Defaults to true
	 * @param noTimelineNotifications when set to true, the timeline will not call the draw() 
	 * method when the timeline changed. Defaults to false
	*/
	constructor(private category: TimelineOverlayCategory, private elementData?: { [key: string]: string }, private autoLayer = true, private noTimelineNotifications = false) {
		this.__id = '' + new Date().getTime() + '_' + Math.floor(Math.random() * 1001);
		this._timeline = undefined;
		this._layer = undefined;

		this.categoryStackOrder = getStackOrder(category);
	}

	public setTimeline(timeline: Timeline): void {
		if (timeline) {
			if (this._timeline) {
				//we already have a timeline
				if (this._timeline.getId() !== timeline.getId()) {
					//we already have another  timeline
					this.onRemove();
					if (this.noTimelineNotifications !== true) {
						this._timeline.unregisterOverlay(this.__id);
					}
					this._timeline = timeline;
					if (this.noTimelineNotifications !== true) {
						this._timeline.registerOverlay(this.__id, this);
					}
					this.onAdd();
					this.draw();
				} else {
					// the given timeline is already set
				}
			} else {
				//we did not have a timeline before
				this._timeline = timeline;
				if (this.noTimelineNotifications !== true) {
					this._timeline.registerOverlay(this.__id, this);
				}
				this.onAdd();
				this.draw();
			}
		} else {
			if (this._timeline) {
				//we have a timeline that needs to be removed
				this.onRemove();
				if (this.noTimelineNotifications !== true) {
					this._timeline.unregisterOverlay(this.__id);
				}
				this._timeline = undefined;
			}
		}
	}
	public getId(): string {
		return this.__id
	}
	public getTimeline(): Timeline {
		return this._timeline;
	}
	protected getLayerMap(): { [key: string]: HTMLElement } {
		if (this._timeline != null) {
			return this._timeline.getLayerMap();
		} else {
			return undefined;
		}
	}
	protected getLayer(): HTMLElement {
		return this._layer;
	}
	protected setLayerHeight(height?: number, layerId?: string): HTMLElement {
		if (this._timeline != null) {
			if (this.autoLayer === true && !layerId) {
				return this._timeline.setLayerHeight(this.getId(), height != undefined ? height : this.getHeight());
			} else {
				if (layerId) {
					return this._timeline.setLayerHeight(layerId, height != undefined ? height : this.getHeight());
				}
			}
		}
		return undefined;
	}
	public getProjection(): Projection {
		if (this._timeline != null) {
			return this._timeline.getProjection();
		} else {
			return undefined;
		}
	};
	public isType(type: number): boolean {
		return (this.getType() & type) > 0;
	};
	//override these
	public getType(): number {
		return OverlayType.Standard;
	};
	protected getHeight(): number {
		return 0;
	};
	protected onAdd(): void {
		if (this.autoLayer === true) {


			let layers = this.getTimeline().getLayers();
			let i = 0;
			while (i < layers.length && getStackOrder((<HTMLElement>layers[i]).dataset.categorystackorder) <= this.categoryStackOrder) {
				i++;
			}

			this._layer = this.getTimeline().addLayer(this.getId(), i, this.category, this.getHeight(), this.isType(OverlayType.Static));

			if (this.elementData) {
				Object.entries(this.elementData).forEach(entry => this._layer.dataset[entry[0]] = entry[1]);
			}
		}
		this.unsubscribe();//to be sure but should(is) not be necesarry
		this.subscribe();
	}
	protected draw(): any {
	}
	protected onRemove(): void {
		this.unsubscribe();
		if (this.autoLayer === true) {
			if (this.getTimeline()) this.getTimeline().removeLayer(this.getId());
			this._layer = null;
		}
	}

	public update(): void {
		let self = this;
		window.requestAnimationFrame(() => self.draw());
	}

	/**
	* override to add event subscriptions
	 */
	protected subscribe(): void {
	};

	protected addSubscribtion(subscription: Subscription): void {
		this.subscriptions.push(subscription);
	};

	private unsubscribe(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions.length = 0;
	};

}


interface TimelineItemBounds extends TemporalCoordinate {
	type: TimelineItemType,
	startPX: number,
	endPX: number,
	widthPX: number
	center?: number,
	centerPX?: number,
}

interface EnrichedTimelineItemBounds extends TimelineItemBounds {
	visiblePX: { start: { layer: number, local: number }, center: { layer: number, local: number }, end: { layer: number, local: number } }
}

interface TimelineDataCluster extends EnrichedTimelineItemBounds {
	dataItemIds: string[],
	dataItems: { [key: string]: Datum },
	count: number
}

interface TimelineItemMetaData extends TimelineDataCluster {
	elemId: string,
	selected: boolean
}
export type TimelineLayoutMap = { [key: string]: TimelineLayout };

export enum TimelineLayout {
	overlap = 'overlap',
	combine = 'combine',
	stack = 'stack'
}
export enum TimelineItemType {
	moment = 1,
	range = 2,
	combined = 4
}

//export type GroupSpecifier = (dataItem: DataItem) => String

/**
 * Standard (none static) Timeline overlay
 */
export class TimelineOverlay extends OverlayView {

	public constructor(category: TimelineOverlayCategory, private className: string, private options: TimelineOverlayOptions, private contentProducer?: TimelineOverlayContentProducer) {
		super(category, options.elementData, options.autoLayer, options.noTimelineNotifications);
	}

	public setProducer(contentProducer: TimelineOverlayContentProducer): TimelineOverlay {
		this.contentProducer = contentProducer;
		this.update();
		return this;
	}

	public getProducer(): TimelineOverlayContentProducer {
		return this.contentProducer;
	}

	protected draw() {
		let drawResult = undefined;
		if (this.getLayer()) {
			if (this.contentProducer) {
				drawResult = this.contentProducer.draw(
					this.getLayer(),
					this.className,
					this.getTimeline().getTimeRange(),
					this.getTimeline().getProjection(),
					this.getTimeline().getAnimationTiming());
			} else {
				this.getLayer().innerHTML = '';
			}
			this.setLayerHeight();
		}

		return drawResult;
	}


	/**
	  @Overide
	 */
	protected getHeight(): number {
		return this.getProducer().getHeight();
	}



	protected subscribe(): void {
		if (this.options.eventHandlers) {
			Object.entries(this.options.eventHandlers).forEach(eventDefinition => {
				this.addSubscribtion(
					delegatedObserver(
						this.getLayer(),
						eventDefinition[0],
						`div.${this.className}`)
						.subscribe(eventDefinition[1]));
			});
		}
	}
	//  /**
	//   * This method returns true when one of the given data items has got a 'selected' property
	//   * that has been set to true.
	//   * @param dataItems The data that is being represented by this overlay
	//   * @return true when one of the data items is selected
	//   */
	//  private needsSelection(dataItems: DataItem[]): boolean {
	//    if (dataItems && dataItems.length > 0) {
	//      return dataItems.filter(dataItem => dataItem && dataItem.selected === true).length > 0
	//    }
	//    return false;
	//  };
	//
	//  /**
	//   * This should be inherited
	//   * @param dataItems The data that is being represented by this overlay
	//   * @param options The options object that will be enriched with drawing options
	//   */
	//  protected setDrawingOptions(dataItems: DataItem[], options: TimelineOverlayOptions) {
	//  };
}

export class TimelineOverlayDatumContentProducer implements TimelineOverlayDataContentProducer {

	private timelineItems: Map<string, TimelineItemMetaData[][]> = undefined;
	private dataItem2timelineItemMap: Map<string, number[]> = undefined;

	private options: TimelineOverlayContentProducerOptions;
	private getData: () => DataMap = () => { return {}; };
	private getAnimationPosition: () => CursorMillis = () => { return undefined; };
	private getLayout: () => TimelineLayout = () => { return undefined; };

	public constructor(private renderer: TimelineRenderer, private datumReader: TimelineOverlayDatumReader, options: TimelineOverlayContentProducerOptions) {
		this.setOptions(options);
	}

	public setOptions(options: TimelineOverlayContentProducerOptions): { elemId: string, dataIds: string[] }[] {
		this.options = Object.assign({ padding: 0, marginGroup: 0, marginRow: 0 }, options);
		this.setLayout(this.options.layout);
		return undefined;
	}

	public setAnimationPosition(animationPosition: CursorMillis): void {
		this.getAnimationPosition = () => animationPosition;
	}

	public setData(data: DataMap): { elemId: string, dataIds: string[] }[] {
		this.getData = () => data;
		return undefined
	}

	public setLayout(layout: TimelineLayout): void {
		this.options.layout = layout;
		this.getLayout = () => layout ? layout : TimelineLayout.stack;
		return undefined
	}

	public findItem(dataItemId: string): { group: string, item: TimelineItemMetaData } {
		if (this.dataItem2timelineItemMap && this.timelineItems) {
			const keys = this.dataItem2timelineItemMap.get(dataItemId);
			if (keys) {
				let items = Array.from(this.timelineItems.entries());
				return { group: items[keys[0]][0], item: items[keys[0]][1][keys[1]][keys[2]] }
			} else {
				return undefined;
			}
		}
		return undefined;
	}

	public findGroupItem(groupName: string, time: number): { group: string, item: TimelineItemMetaData } {
		if (this.timelineItems && this.timelineItems.get(groupName)) {
			let timelineItemsGroup = this.timelineItems.get(groupName);
			let items = timelineItemsGroup.flat().filter(tlItem => {
				if (tlItem.start == undefined) {
					return tlItem.end == undefined || tlItem.end > time;
				} else {
					return tlItem.start <= time && (tlItem.end == undefined || tlItem.end > time);
				}
			});
			if (items.length > 0) {
				return { group: groupName, item: items[0] };
			}
		}
		return undefined;
	}

	public getItem(elemId: string): { group: string, item: TimelineItemMetaData } {
		if (this.timelineItems) {
			let item = Array.from(this.timelineItems.entries())
				.reduce((accu, entry) => {
					if (!accu.item) {
						accu.group = entry[0];
						//following line should produce an array of length 0 or 1 (1 being the elemMeta with correct elemId)
						let elemMetaList = entry[1].flat().filter(elemMeta => elemMeta.elemId === elemId);
						if (elemMetaList.length > 0) {
							accu.item = elemMetaList[0];
							if (elemMetaList.length > 1) {
								console.trace(`Multiple (${elemMetaList.length}) elements found in timeline-overlay for elemId ${elemId} while only one is expected.`);
							}
						}
					}
					return accu;
				}, <{ group: string, item: TimelineItemMetaData }>{});


			if (!item.item) {
				console.trace(`Expected to find element ${elemId} in timeline-overlay but it was not found.`);
			} else {
				return item;
			}
		}
		return undefined;
	}

	public highlight(container: HTMLElement): void {
		Object.entries(this.getData()).forEach(entry => {
			let group = entry[0];
			let data = entry[1];
			let dataIds = data.getMeta().selected;

			if (this.timelineItems) {
				let timelineItemsGroup = this.timelineItems.get(group);
				if (timelineItemsGroup) {
					timelineItemsGroup.flat().forEach(tlItem => tlItem.selected = false);
				}
			}

			let elemIds: string[] = dataIds.reduce((elemIds, dataId) => {
				let elem = this.findItem(dataId);
				if (elem && !elemIds.includes(elem.item.elemId)) {
					elemIds.push(elem.item.elemId);
					elem.item.selected = true
				}
				return elemIds;
			}, []);

			container.querySelectorAll(`.${group}`).forEach(item => {
				if (elemIds.includes(item.id)) {
					item.classList.add('highlight');
				} else {
					item.classList.remove('highlight');
				}
			});

		});
	}

	public getHeight(): number {
		let totalContentHeight = this.timelineItems ? Array.from(this.timelineItems.values()).reduce((height, rows, groupIndex) => {
			height += (groupIndex > 0 ? this.options.marginGroup : 0) + rows.length * this.renderer.size().height + (rows.length > 1 ? ((rows.length - 1) * this.options.marginRow) : 0);
			return height;
		}, 0) : 0;

		return (this.options.padding * (totalContentHeight > 0 ? 2 : 0)) + totalContentHeight;
	}


	public draw(
		layer: HTMLElement,
		className: string,
		timelineRange: TimelineRange,
		projection: Projection,
		animationTiming: CursorMillis[]): VisualId[] {

		let layoutResult = this.buildTimelineItems(this.getData(), timelineRange, projection, this.getAnimationPosition());

		//identify all TimelineItems that do not occur in the new list anymore
		let toRemove = this.findObsoleteTimelineItems(layoutResult.tlItems);
		// remove
		toRemove.map(elemId => document.getElementById(elemId)).filter(elem => elem != undefined).forEach(elem => elem.parentElement.removeChild(elem));

		this.timelineItems = layoutResult.tlItems;
		this.dataItem2timelineItemMap = layoutResult.dataItem2tlItemMap;
		// update/create
		let groupStartRows = Array.from(this.timelineItems.values()).reduce((accu, rows, groupIndex) => {
			accu.groupStartRows.push(accu.y);
			accu.y += this.options.marginGroup + rows.length * this.renderer.size().height + (rows.length > 1 ? ((rows.length - 1) * this.options.marginRow) : 0)
			return accu;
		}, { y: this.options.padding, groupStartRows: [] }).groupStartRows;

		Array.from(this.timelineItems.entries()).forEach((groupEntry, groupIndex) => {
			let rows = groupEntry[1];
			let groupY = groupStartRows[groupIndex];
			let itemsClassName = `${className} ${groupEntry[0]}`;
			rows.forEach((row, rowIndex) => {
				//let rowY = groupY + ( rowIndex * this.renderer.size().height ) + (rowIndex > 0 ? this.options.marginRow : 0 );
				let rowY = groupY + (rowIndex * (this.renderer.size().height + this.options.marginRow));
				row.forEach((tlItem, itemIndex) => {
					let startAnimation = tlItem.start;
					const overlayScript = animationTiming.filter(cursorMillis => cursorMillis.center >= startAnimation && cursorMillis.center < tlItem.end);

					let domElem = document.getElementById(tlItem.elemId);
					if (!domElem) {
						domElem = this.buildDOMElem(tlItem, itemsClassName);
						this.layoutDOMElem(tlItem, projection, overlayScript, rowY, domElem);
						layer.appendChild(domElem);
					} else {
						this.layoutDOMElem(tlItem, projection, overlayScript, rowY, domElem);
					}
				});
			});
		});

		return layoutResult.tlItemList;
	}

	private buildTimelineItems(dataMap: DataMap, timelineRange: TimelineRange, projection: Projection, animationPosition: CursorMillis): TimelineLayoutResult {
		let dataItem2timelineItemMap: Map<string, number[]> = new Map();

		let timelineItems = Array.from(Object.entries(dataMap))
			.map(groupEntry => [groupEntry[0], this.mapDataItemsToTimelineItems(groupEntry[1].getData(), timelineRange, projection, groupEntry[1].getMeta().selected)])
			.map(groupEntry2 => [<string>groupEntry2[0], this.stackTimeLineItems(<TimelineItemMetaData[]>groupEntry2[1])])
			.reduce((map, entry) => map.set(<string>entry[0], <TimelineItemMetaData[][]>entry[1]), new Map<string, TimelineItemMetaData[][]>());

		let tlReturnItemList: { elemId: string, dataIds: string[] }[] = [];
		Array.from(timelineItems.entries()).forEach((entry, groupIndex) => {
			let group = entry[0];
			let rows = entry[1];
			rows.forEach((row, rowIndex) => {
				row.forEach((tlItem, tlItemIndex) => {
					let tlItemReference = [groupIndex, rowIndex, tlItemIndex];
					tlItem.dataItemIds.forEach(dataItemId => dataItem2timelineItemMap.set(dataItemId, tlItemReference));
					tlReturnItemList.push({ elemId: tlItem.elemId, dataIds: [].concat(tlItem.dataItemIds) });

				});
			});
		});
		return { tlItems: timelineItems, dataItem2tlItemMap: dataItem2timelineItemMap, tlItemList: tlReturnItemList };
	}
	/**
   * For each group the following steps are performed:
   * - sort dataItems on there timing properties (roughly speaking on start millis)
   * - calculate for each data item the graphical width and position on the timeline
   * - then cluster all data items  (now mapped to TimelineItemMetaData) that would
   *   visually overlap. This is only done when this.options.layout is set to Layout.combine .
   *   When this option is set to another value, each item is a cluster on its own.
   * - for each cluster construct an id that will be used as the HTMLElement id.
   *   The ids are deterministic, based on dataItem.id, dataItem.start and dataItem.end present
   *   in that cluster
   *   
   * @param dataItems the dataItems in one group that should be mapped to (combined) TimelineItems
   */
	private mapDataItemsToTimelineItems(dataItems: Datum[], timelineRange: TimelineRange, projection: Projection, selectedIds: string[]): TimelineItemMetaData[] {
		const size = this.renderer.size();
		const visibleRange = {
			start: projection.fromMillisToPoint(timelineRange.visible.start),
			end: projection.fromMillisToPoint(timelineRange.visible.end)
		};

		let timelineItems = dataItems.sort(this.datumReader.compareByTime)
			.map(dataItem => { return { dataItem: dataItem, tlItem: this.mapDataItemToTimelineItemBounds(dataItem, projection, size), selected: selectedIds.includes(dataItem.id) }; });

		let combinedTimelineItems: TimelineItemMetaData[];
		if (this.getLayout() === TimelineLayout.combine) {
			combinedTimelineItems = timelineItems.reduce((clusters, item) => {
				//take last cluster  
				let dataCluster = clusters.length > 0 ? clusters[clusters.length - 1] : undefined;

				if (dataCluster && (item.tlItem.start == undefined || dataCluster.end == undefined || item.tlItem.start < dataCluster.end)) {
					//overlap
					//add item
					dataCluster.type = dataCluster.type | item.tlItem.type | TimelineItemType.combined;
					dataCluster.dataItems[item.dataItem.id] = item.dataItem;
					dataCluster.dataItemIds.push(item.dataItem.id);
					dataCluster.count++;
					dataCluster.selected = dataCluster.selected || item.selected;

					dataCluster.center = undefined;
					dataCluster.centerPX = undefined;

					if (item.tlItem.center && dataCluster.end) {
						// a new moment added to finite range
						if (item.tlItem.end > dataCluster.end) {
							dataCluster.end = item.tlItem.end;
							dataCluster.endPX = item.tlItem.endPX;
						}

					} else {
						// a range added to another range
						//adjust start
						if (dataCluster.start != undefined && (item.tlItem.start == undefined || dataCluster.start > item.tlItem.start)) {
							dataCluster.start = item.tlItem.start;
							dataCluster.startPX = item.tlItem.startPX;
						}
						//adjust end
						if (dataCluster.end != undefined && (item.tlItem.end == undefined || dataCluster.end < item.tlItem.end)) {
							dataCluster.end = item.tlItem.end;
							dataCluster.endPX = item.tlItem.endPX;
						}
					}

					dataCluster.widthPX = dataCluster.endPX - dataCluster.startPX;
				} else {
					//starts after data cluster
					if (dataCluster) {
						this.addVisibilityParameters(dataCluster, visibleRange);
						dataCluster.elemId = this.mapTimelineDataClusterToDOMId(dataCluster);
					}
					let dim: { [key: string]: Datum } = {};
					dim[item.dataItem.id] = item.dataItem;
					clusters.push(Object.assign(item.tlItem, { dataItems: dim, dataItemIds: [item.dataItem.id], count: 1, elemId: undefined, visiblePX: undefined, selected: item.selected }));
				}
				return clusters;
			}, <TimelineItemMetaData[]>[]);
			let dataCluster = combinedTimelineItems.length > 0 ? combinedTimelineItems[combinedTimelineItems.length - 1] : undefined;
			if (dataCluster) {
				this.addVisibilityParameters(dataCluster, visibleRange);
				dataCluster.elemId = this.mapTimelineDataClusterToDOMId(dataCluster);
			}
		} else {
			combinedTimelineItems = timelineItems.map(item => {
				let dim: { [key: string]: Datum } = {};
				dim[item.dataItem.id] = item.dataItem;
				let dataCluster = Object.assign(item.tlItem, { dataItems: dim, dataItemIds: [item.dataItem.id], count: 1, elemId: undefined, visiblePX: undefined, selected: item.selected });
				this.addVisibilityParameters(dataCluster, visibleRange);
				dataCluster.elemId = this.mapTimelineDataClusterToDOMId(dataCluster);
				return dataCluster
			});
		}
		return combinedTimelineItems;
	}

	private mapDataItemToTimelineItemBounds(dataItem: Datum, projection: Projection, size: Size,): TimelineItemBounds {
		let st: number, spx: number, ct: number, cpx: number, et: number, epx: number, wpx: number, type: TimelineItemType;

		if (this.datumReader.isMoment(dataItem)) {
			type = TimelineItemType.moment;
			ct = this.datumReader.getTime(dataItem);
			wpx = size.width;
			cpx = projection.fromMillisToPoint(ct);
			spx = cpx - wpx / 2;
			epx = spx + wpx;
			st = projection.fromPointToMillis(spx);
			et = projection.fromPointToMillis(epx);
		} else {
			type = TimelineItemType.range;
			st = this.datumReader.getStart(dataItem);
			et = this.datumReader.getEnd(dataItem);
			spx = projection.fromMillisToPoint(st, false, true);// this call return 0 when st is undefined
			epx = projection.fromMillisToPoint(et, false, false);//this call return timeline pixel width when et is undefined 
			wpx = epx - spx;
			ct = undefined;
			cpx = undefined;
		}
		return {
			type: type,
			time: undefined,
			start: st,
			startPX: spx,
			end: et,
			endPX: epx,
			widthPX: epx - spx,
			center: ct,
			centerPX: cpx
		};
	}
	private addVisibilityParameters(tlBounds: TimelineItemBounds, visiblePXRange: { start: number, end: number }): EnrichedTimelineItemBounds {

		let visibilityPX: { start: { layer: number, local: number }, center: { layer: number, local: number }, end: { layer: number, local: number } };

		if (tlBounds.startPX < visiblePXRange.end && tlBounds.endPX > visiblePXRange.start) {
			let start = tlBounds.startPX <= visiblePXRange.start ? { layer: visiblePXRange.start, local: visiblePXRange.start - tlBounds.startPX } : { layer: tlBounds.startPX, local: 0 };
			let end = tlBounds.endPX >= visiblePXRange.end ? { layer: visiblePXRange.end, local: visiblePXRange.end - tlBounds.startPX } : { layer: tlBounds.endPX, local: tlBounds.endPX - tlBounds.startPX };
			let center = { layer: (start.layer + end.layer) / 2, local: (start.local + end.local) / 2 };
			visibilityPX = { start: start, center: center, end: end };
		} else {
			//the bounds are not in view
			visibilityPX = { start: { layer: undefined, local: undefined }, center: { layer: undefined, local: undefined }, end: { layer: undefined, local: undefined } };
		}

		return Object.assign(tlBounds, { visiblePX: visibilityPX });
	}
	private mapTimelineDataClusterToDOMId(cluster: TimelineDataCluster): string {
		return 'tl' + createElemSelector(
			[].concat(cluster.dataItemIds).sort().map(id => {
				let dataItem = cluster.dataItems[id];
				return this.datumReader.temporalHash(dataItem);
			})
		);
	}
	private stackTimeLineItems(tlItems: TimelineItemMetaData[]): TimelineItemMetaData[][] {
		let rows: TimelineItemMetaData[][] = [];
		if (this.getLayout() === TimelineLayout.stack) {

			tlItems.forEach(tlItem => {
				let row: TimelineItemMetaData[] = undefined;

				if (tlItem.start != undefined && tlItem.end != undefined) {//period (maybe also 'since' cases?? I.e. tlItem.start != undefined)


					//find the first row that ends (in pixels) before or at the position that the current item starts (in pixels)
					let rowCandidates = rows.map((rowItems, rowIndex) => {
						return { index: rowIndex, dist: rowItems.length === 0 ? 0 : tlItem.startPX - rowItems[rowItems.length - 1].endPX }
					})
						.filter(x => x.dist >= 0);

					row = rowCandidates.length > 0 ? rows[rowCandidates[0].index] : undefined;
				}

				if (!row) {
					row = [];
					rows.push(row);
				}
				row.push(tlItem);
			});
		} else {
			rows = [tlItems];
		}
		return rows;
	}

	private findObsoleteTimelineItems(timelineItems: Map<string, TimelineItemMetaData[][]>): string[] {
		let toRemove: string[] = [];
		if (this.timelineItems) {
			let pluckId = (item: TimelineItemMetaData) => item.elemId;
			Array.from(this.timelineItems.entries()).forEach(groupEntry => {
				let newGroup = timelineItems.get(groupEntry[0]);
				if (newGroup) {
					let flatNewGroup = newGroup.flat();
					toRemove = toRemove.concat(groupEntry[1].flat().filter(item => flatNewGroup.findIndex(newItem => newItem.elemId === item.elemId) === -1).map(pluckId));
				} else {
					//complete group must be removed
					toRemove = toRemove.concat(groupEntry[1].flat().map(pluckId));
				}
			});
		}

		return toRemove;
	}
	private layoutDOMElem(meta: TimelineItemMetaData, projection: Projection, animationScript: CursorMillis[], y: number, domElem?: HTMLElement): HTMLElement {
		let elem = domElem && domElem.id === meta.elemId ? domElem : <HTMLElement>document.getElementById(meta.elemId);

		elem.style.left = meta.startPX + 'px';
		elem.style.bottom = y + 'px';

		elem.style.width = meta.widthPX + 'px';
		elem.style.height = this.renderer.size().height + 'px';
		const left = meta.startPX;
		const millisToLocalPixel = (millis: number): number => projection.fromMillisToPoint(millis) - left;

		if (meta.selected) {
			elem.classList.add('highlight');
		} else {
			elem.classList.remove('highlight');
		}

		if (this.renderer) {
			this.renderer.draw(elem, meta, this.renderer.size(), animationScript, millisToLocalPixel);
		}
		return elem;
	}

	private buildDOMElem(meta: TimelineItemMetaData, className: string): HTMLElement {
		return <HTMLElement>markup2element(`<div id="${meta.elemId}" class="tlmd-overlay-marker ${className}" ></div>`);
	}
}
